// Квесты в МК: каталог, карта квеста (10 игр), карточка игры (3 части), прохождение (общий модуль player.ts) и итог.
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Screen, Busy, toast } from '../app/ui.tsx';
import { useMe } from '../app/state.tsx';
import { api } from '../platform/api.ts';
import { t, errText, neurons } from '../i18n/index.ts';
import { mountPlayer, type Start, type Texts } from './player.ts';

const rub = (kop: number) => `${(kop / 100).toLocaleString('ru-RU')} ₽`;

// Возврат с оплаты Т-Банка: ждём уведомление банка (до минуты), потом — к игре
export function PayReturn() {
  const nav = useNavigate();
  const ready = useProfile();
  const [search] = useSearchParams();
  const order = Number(search.get('order'));
  const back = '/' + (search.get('back') ?? 'profile').replace(/^\/+/, '');
  const [state, setState] = useState<'wait' | 'paid' | 'fail' | 'slow'>(search.get('fail') ? 'fail' : 'wait');
  useEffect(() => {
    if (!ready || state !== 'wait') return;
    let n = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const r = await api<{ status: string }>('order_status', { order });
        if (r.status === 'paid') { catalogCache = null; return setState('paid'); }
        if (r.status === 'cancelled') return setState('fail');
      } catch { /* сеть — пробуем ещё */ }
      if (++n >= 30) return setState('slow');
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [ready, order, state]);
  return (
    <Screen title={t(`shop.${state}Title`)}>
      {state === 'wait' && <p><span className="spin" /></p>}
      <p className="muted">{t(`shop.${state}Text`)}</p>
      <button type="button" className="btn wide" onClick={() => nav(back, { replace: true })}>{t('shop.back')}</button>
    </Screen>
  );
}

type CGame = { slug: string; position: number; parts: number; state: 'new' | 'active' | 'finished'; score: number | null; theme: string | null; cover: string | null };
type CClassic = { slug: string; title: string; format: string; difficulty: number | null; state: string; score: number | null; draft: boolean };
type CQuest = { slug: string; title: string; emoji: string | null; color: string | null; theme_hint: string | null; access: string; draft: boolean; games: CGame[] };

// без профиля (ссылка из ролика) — сразу гость: первая глава бесплатно и без регистрации
function useProfile() {
  const { me, reload } = useMe();
  const [ok, setOk] = useState(!!me);
  useEffect(() => {
    if (me) { setOk(true); return; }
    api('auth_guest').then(reload).catch(e => toast(errText(e)));
  }, [me, reload]);
  return ok;
}

let catalogCache: CQuest[] | null = null;
let classicCache: CClassic[] = [];
async function loadCatalog(fresh = false) {
  if (!catalogCache || fresh) { const r = await api<{ quests: CQuest[]; classic: CClassic[] }>('catalog'); catalogCache = r.quests; classicCache = r.classic ?? []; }
  return catalogCache;
}

export function Catalog() {
  const [quests, setQuests] = useState<CQuest[] | null>(catalogCache);
  useEffect(() => { loadCatalog(true).then(setQuests).catch(e => toast(errText(e))); }, []);
  if (!quests) return <p><span className="spin" /></p>;
  if (!quests.length && !classicCache.length) return <p className="muted">{t('home.soon')}</p>;
  return (
    <>
    <div className="quests">
      {quests.map(q => {
        const solved = q.games.filter(g => g.state === 'finished').length;
        return (
          <Link key={q.slug} to={`/q/${q.slug}`} className="quest" style={{ ['--qc' as any]: q.color ?? 'var(--brand)' }}>
            <span className="quest-emoji" aria-hidden>{q.emoji}</span>
            <span className="quest-title">{q.title}{q.draft ? ` · ${t('quest.draft')}` : ''}</span>
            <span className="quest-meta">{t('quest.progress', { n: solved, total: q.games.length })}</span>
          </Link>
        );
      })}
    </div>
    {classicCache.length > 0 && (
      <>
        <h2>{t('home.classic')}</h2>
        <ul className="classic">
          {classicCache.map(g => (
            <li key={g.slug}>
              <Link to={`/g/${g.slug}`} className={`classic-item ${g.state}`}>
                <span>{g.title}{g.draft ? ` · ${t('quest.draft')}` : ''}</span>
                <span className="small muted">{g.state === 'finished' ? t('quest.points', { n: g.score ?? 0 }) : g.state === 'active' ? t('part.continue') : ''}</span>
              </Link>
            </li>
          ))}
        </ul>
      </>
    )}
    </>
  );
}

export function QuestMap() {
  const { slug } = useParams();
  const ready = useProfile();
  const [q, setQ] = useState<CQuest | null>(null);
  useEffect(() => {
    if (ready) loadCatalog(true).then(all => setQ(all.find(x => x.slug === slug) ?? null)).catch(e => toast(errText(e)));
  }, [ready, slug]);
  if (!q) return <Screen back="/"><span className="spin" /></Screen>;
  return (
    <Screen back="/" title={`${q.emoji ?? ''} ${q.title}`}>
      {q.theme_hint && <p className="muted">{t('quest.hint', { hint: q.theme_hint })}</p>}
      <div className="games">
        {q.games.map(g => (
          <Link key={g.slug} to={`/g/${g.slug}`} className={`game ${g.state}`}>
            {g.cover ? <img src={g.cover} alt="" /> : <span className="game-n">{g.position}</span>}
            <span className="game-title">{g.theme ?? t('quest.game', { n: g.position })}</span>
            {g.state === 'finished' && <span className="game-score">{t('quest.points', { n: g.score ?? 0 })}</span>}
          </Link>
        ))}
      </div>
    </Screen>
  );
}

type Info = { balance: number; replay_price: number; game: { slug: string; title: string; theme: string | null; format: string; format_title: string; description: string | null };
              parts: { n: number; kind: string; title: string | null; is_free: boolean; state: string; score: number | null;
                       access: 'free' | 'have' | 'buy' | 'account' | 'paywall'; price: number | null }[] };

export function GameCard() {
  const { slug } = useParams();
  const ready = useProfile();
  const nav = useNavigate();
  const [info, setInfo] = useState<Info | null>(null);
  const [game, setGame] = useState<{ price_kop: number; pct: number } | null>(null);   // «Игра» поштучно — если оплата картой включена
  const [useN, setUseN] = useState(true);   // списать нейроны (до pct % цены)
  const load = () => api<Info>('game_info', { slug }).then(setInfo);
  useEffect(() => {
    api<{ card: boolean; neuron_pct: number; products: { code: string; price_kop: number }[] }>('products').then(r => {
      const g = r.card ? r.products.find(p => p.code === 'game') : null;
      setGame(g ? { price_kop: g.price_kop, pct: r.neuron_pct } : null);
    }).catch(() => {});
  }, []);
  const buyGame = async () => {
    try { const r = await api<{ url: string }>('buy', { product: 'game', slug, ...(useN && off > 0 ? { neurons: off } : {}) }); location.href = r.url; }
    catch (e) { toast(errText(e)); }
  };
  useEffect(() => { if (ready) load().catch(e => { toast(errText(e)); nav('/'); }); }, [ready, slug, nav]);   // eslint-disable-line
  if (!info) return <Screen back="/"><span className="spin" /></Screen>;
  const buy = async (n: number) => {
    try { await api('part_unlock', { slug, part: n }); await load(); nav(`/g/${slug}/${n}`); } catch (e) { toast(errText(e)); }
  };
  // скидка нейронами: 1 нейрон = 1 ₽, не больше pct % цены и баланса
  const off = game ? Math.min(info.balance, Math.floor(game.price_kop * game.pct / 100 / 100)) : 0;
  const sub = (p: Info['parts'][number]) =>
    p.state === 'finished' ? t('quest.points', { n: p.score ?? 0 }) : p.state === 'locked' ? t('part.locked')
      : p.access === 'free' ? t('part.free') : p.access === 'have' ? (p.is_free ? t('part.free') : t('part.bought'))
      : p.access === 'account' ? t('part.login') : t('part.paid');
  return (
    <Screen back title={info.game.theme ?? info.game.title}>
      {info.game.format === 'quest' ? (!info.game.theme && <p className="muted">{t('quest.secret')}</p>)
        : info.game.description && <p className="muted">{info.game.description}</p>}
      <ol className="parts">
        {info.parts.map(p => (
          <li key={p.n} className={`part ${p.state}`}>
            <div>
              <b>{info.game.format === 'quest' ? t(`part.${p.kind}`) : info.game.format_title}</b>
              <div className="small muted">{sub(p)}</div>
            </div>
            {p.state === 'open' && (p.access === 'buy' && p.price != null
              ? <Busy onClick={() => buy(p.n)}>{t('part.buy', { price: neurons(p.price) })}</Busy>
              : p.access === 'account' ? <Link className="btn ghost" to="/login">{t('home.login')}</Link>
              : p.access === 'paywall' ? null
              : <Link className="btn" to={`/g/${slug}/${p.n}`}>{t('part.play')}</Link>)}
            {p.state === 'active' && <Link className="btn" to={`/g/${slug}/${p.n}`}>{t('part.continue')}</Link>}
            {p.state === 'finished' && (p.access === 'account'
              ? <span aria-label={t('part.done')}>✅</span>
              : <Link className="btn ghost" to={`/g/${slug}/${p.n}?replay=1`}>{t('part.replay', { price: neurons(info.replay_price) })}</Link>)}
          </li>
        ))}
      </ol>
      {info.balance > 0 && <p className="small muted">{t('part.balance', { n: neurons(info.balance) })}</p>}
      {game && info.parts.some(p => p.access === 'paywall' || p.access === 'buy') && (
        <section className="card">
          <p className="muted">{t(`shop.gameText.${info.game.format === 'quest' ? 'quest' : 'classic'}`)}</p>
          {off > 0 && (
            <label className="check">
              <input type="checkbox" checked={useN} onChange={e => setUseN(e.target.checked)} />
              {t('shop.useNeurons', { n: neurons(off), rub: rub(off * 100) })}
            </label>
          )}
          <Busy onClick={buyGame}>{t('shop.buyGame', { price: rub(game.price_kop - (useN ? off * 100 : 0)) })}</Busy>
        </section>
      )}
    </Screen>
  );
}

// шаблоны для модуля — с {подстановками} как есть (их заполняет player.ts)
const keep = new Proxy({}, { get: (_, k) => `{${String(k)}}` }) as Record<string, string>;
const raw = (path: string) => t(path, keep);
const playerTexts = (): Texts => ({
  question: raw('play.question'), score: raw('play.score'), answerPlaceholder: t('play.answer'), themePlaceholder: t('play.theme'),
  submit: t('play.submit'), next: t('play.next'), finish: t('play.finish'), correct: t('play.correct'), wrong: t('play.wrong'),
  timeout: t('play.timeout'), points: raw('play.points'), rightAnswer: raw('play.rightAnswer'), themeOptions: t('play.themeOptions'), error: t('err.server'),
  hint: raw('hint.hint'), hintNames: { fifty: t('hint.fifty'), letter: t('hint.letter'), theme: t('hint.theme') },
  retry: raw('hint.retry'), time: raw('hint.time'), fiftyUsed: t('hint.fiftyUsed'), letterUsed: raw('hint.letterUsed'), themeUsed: raw('hint.themeUsed'),
  retryOn: t('hint.retryOn'), retryUsed: t('hint.retryUsed'), timeUsed: raw('hint.timeUsed'), notEnough: raw('hint.notEnough'), price: neurons,
});

export function Play() {
  const { slug, part } = useParams();
  const [search] = useSearchParams();
  const replay = search.get('replay') === '1';
  const nav = useNavigate();
  const ready = useProfile();
  const box = useRef<HTMLDivElement>(null);
  const [start, setStart] = useState<(Start & { game: { title: string } }) | null>(null);
  const [result, setResult] = useState<any>(null);

  useEffect(() => { setResult(null); setStart(null); }, [slug, part]);
  useEffect(() => {
    if (!ready) return;
    api('play_start', { slug, part: Number(part), ...(replay ? { replay: true } : {}) }).then(setStart).catch(e => { toast(errText(e)); nav(`/g/${slug}`, { replace: true }); });
  }, [ready, slug, part, nav, replay]);

  useEffect(() => {
    if (!start || !box.current || result) return;
    const id = start.state.play_id;
    return mountPlayer(box.current, start, {
      serve: q => api('play_serve', { play_id: id, q }),
      answer: (q, answer, stage) => api('play_answer', { play_id: id, q, answer, ...(stage ? { stage } : {}) }),
      hint: (q, kind) => api('play_hint', { play_id: id, q, ...(kind ? { kind } : {}) }),
      time: q => api('play_time', { play_id: id, q }),
    }, { texts: playerTexts(), onFinish: r => { catalogCache = null; setResult(r); }, onError: e => toast((e as any)?.code === 'not_enough' && (e as any)?.message ? (e as any).message : errText(e)) });
  }, [start, result]);

  if (result) return <Result slug={slug!} r={result} />;
  return (
    <Screen back={`/g/${slug}`} title={start ? ((start as any).game?.format === 'quest' ? t(`part.${start.part.kind}`) : (start as any).game?.title) : undefined}>
      {!start && <span className="spin" />}
      <div ref={box} />
    </Screen>
  );
}

function Result({ slug, r }: { slug: string; r: any }) {
  const nav = useNavigate();
  const { me } = useMe();
  return (
    <Screen back={`/g/${slug}`} title={t('result.title')}>
      {r.theme && (
        <section className="card accent">
          <div className="small muted">{t(r.result.theme_how === 'none' ? 'result.themeWas' : 'result.themeSolved')}</div>
          <h2>{r.theme}</h2>
        </section>
      )}
      <section className="card">
        <div className="big">{t('result.score', { score: r.result.score, max: r.result.max_score })}</div>
        <div className="muted">{t('result.correct', { n: r.result.correct, total: r.result.total })}</div>
      </section>
      {r.next && (
        <section className="card">
          <h2>{t('result.next', { part: t(`part.${r.next.kind}`) })}</h2>
          {me?.profile.is_guest && !r.next.is_free
            ? <><p className="muted">{t('result.needAccount')}</p><button type="button" className="btn" onClick={() => nav('/login')}>{t('home.login')}</button></>
            : <Link className="btn" to={`/g/${slug}/${r.next.n}`}>{t('part.play')}</Link>}
        </section>
      )}
      <details className="card">
        <summary>{t('result.review')}</summary>
        <ol className="review">
          {r.review.map((q: any) => (
            <li key={q.n}><div>{q.text}</div><b>{q.answer}</b>{q.explanation && <div className="small muted">{q.explanation}</div>}</li>
          ))}
        </ol>
      </details>
      <button type="button" className="btn ghost wide" onClick={() => nav(`/g/${slug}`)}>{t('result.back')}</button>
    </Screen>
  );
}
